import type { ApiResponse } from "./types";
import { log } from "../util/logger";

class ApiClient {
  private readonly baseUrl: string;
  private readonly defaultHeaders: HeadersInit;
  private isAddingError = false; // Prevent recursive error reporting

  constructor(baseUrl = "/api") {
    this.baseUrl = baseUrl;
    this.defaultHeaders = {
      "Content-Type": "application/json",
    };
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<ApiResponse<T | undefined>> {
    const url = `${this.baseUrl}${endpoint}`;
    const config: Record<string, unknown> = {
      ...options,
      headers: {
        ...this.defaultHeaders,
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        throw new Error(
          `Code: ${response.status.toString()}`,
        );
      }

      const returnData = {
        data: undefined,
        success: true,
        message: response.statusText,
        status: response.status,
        timestamp: new Date().toISOString(),
      };

      if (response.status >= 204) {
        return returnData;
      }

      const data = await response.json();
      returnData.data = data;
      return returnData;
    } catch (error) {
      if (!this.isAddingError) {
        await this.logApiError(error, endpoint, config, url);
      }

      return {
        data: undefined,
        success: false,
        message: "Contact support",
        timestamp: new Date().toISOString(),
      };
    }
  }

  private async logApiError(
    error: unknown,
    endpoint: string,
    config: Record<string, unknown>,
    url: string,
  ): Promise<void> {
    this.isAddingError = true;

    try {
      const { useErrorStore } = await import("../stores/error-store");
      if (useErrorStore && useErrorStore.getState()) {
        useErrorStore.getState().addError(error);
      }
    } catch (storeError) {
      log.error("storeError", undefined, storeError);
    } finally {
      this.isAddingError = false;
      log.error(
        "API Error",
        {
          metadata: {
            url,
            endpoint,
            config,
          },
        },
        error,
      );
    }
  }

  async get<T>(endpoint: string, options?: RequestInit) {
    return this.request<T>(endpoint, { ...options, method: "GET" });
  }

  async post<T, U>(endpoint: string, data?: U, options?: RequestInit) {
    return this.request<T>(endpoint, {
      ...options,
      method: "POST",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T, U>(endpoint: string, data?: U, options?: RequestInit) {
    return this.request<T>(endpoint, {
      ...options,
      method: "PUT",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(endpoint: string, options?: RequestInit) {
    return this.request<T>(endpoint, { ...options, method: "DELETE" });
  }
}

export default ApiClient;
